import { graphql, navigate } from 'gatsby';
import { type FC, useEffect } from 'react';
import { useCookie } from 'react-use';

import { contentfulProps } from 'assets/mockData/emptyTypes';

import Section from 'molecules/Section/Section';

import CardDeck from 'components/CardDeck/CardDeck';
import DownloadExitHero from 'components/Download/DownloadExitHero';
import DownloadHero from 'components/Download/DownloadHero';

import Layout from 'layouts/Layout';
import LayoutSection from 'layouts/LayoutSection';
import SEO from 'layouts/SEO';

import { toRichText } from 'utils/functions';
import useHasLoggedIn from 'utils/useHasLoggedIn';

import type { HeadFC, PageProps } from 'gatsby';
import type {
  ContentfulComponentHeading,
  ContentfulLayoutSection,
  ContentfulTemplateBlogPostConnection,
  ContentfulTemplateDownload,
} from 'graphqlTypes';

const downloadBlogsSectionHeading: ContentfulComponentHeading = {
  ...contentfulProps,
  __typename: 'ContentfulComponentHeading',
  heading: 'What else are you looking for?',
  body: {
    raw: toRichText(
      'Scale your business and build your team — no matter which way it grows. Access the tools, perks, and resources to help you stay compliant and grow in all 50 states.',
    ),
  },
  headingType: 'H2',
  headingSizeDesktop: 'lg',
  headingSizeTablet: 'md',
  headingSizeMobile: 'sm',
  alignment: 'Centered',
};

interface DownloadData {
  pageData: ContentfulTemplateDownload;
  downloadFeatureGrids: ContentfulLayoutSection;
  recentBlogPosts: ContentfulTemplateBlogPostConnection;
}

interface DownloadContext {
  isExitPage?: boolean;
}

const Download: FC<PageProps<DownloadData, DownloadContext>> = ({
  data: { pageData, downloadFeatureGrids, recentBlogPosts },
  pageContext: { isExitPage },
  location: { pathname },
}) => {
  const [formsSubmittedSlugs] = useCookie('forms_submitted_slugs');
  const hasLoggedIn = useHasLoggedIn();

  useEffect(() => {
    const userHasSubmittedForm: boolean =
      formsSubmittedSlugs && JSON.parse(formsSubmittedSlugs)?.includes(pathname.replace('/thank-you', ''));

    const shouldBeOnExit = hasLoggedIn || userHasSubmittedForm;

    if (!isExitPage && shouldBeOnExit) {
      navigate('./thank-you', { replace: true });

      return;
    }

    if (isExitPage && !shouldBeOnExit) {
      navigate(`${pathname.replace('/thank-you', '')}`, { replace: true });

      return;
    }
  }, []);

  return (
    <Layout>
      {isExitPage ? <DownloadExitHero {...pageData} /> : <DownloadHero {...pageData} />}
      {downloadFeatureGrids && <LayoutSection {...downloadFeatureGrids} />}
      {recentBlogPosts && (
        <Section id="recent-blogs-section" backgroundColor="Gray 100">
          <CardDeck
            {...contentfulProps}
            backgroundIsDark={false}
            headingContent={downloadBlogsSectionHeading}
            cards={recentBlogPosts.nodes}
          />
        </Section>
      )}
    </Layout>
  );
};

export default Download;

export const Head: HeadFC<DownloadData> = ({
  data: {
    pageData: { seo },
  },
  location: { pathname },
}) => <SEO pathname={pathname} {...seo} />;

export const pageQuery = graphql`
  query templateDownloadQuery($slug: String!) {
    pageData: contentfulTemplateDownload(slug: { eq: $slug }) {
      ...contentfulTemplateDownload
    }
    downloadFeatureGrids: contentfulLayoutSection(contentful_id: { eq: "1gzxEswOx0AE9VzL1d7IBA" }) {
      ...contentfulLayoutSection
    }
    recentBlogPosts: allContentfulTemplateBlogPost(
      limit: 4
      sort: { publishDate: DESC }
      filter: { publishDate: { ne: null }, slug: { glob: "!**/*dummy*" } }
    ) {
      nodes {
        ...contentfulTemplateBlogPost
      }
    }
  }
`;
